import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUploadStore } from '../store/uploadStore';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Progress } from './ui/progress';
import axios from 'axios';
import {
  ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle, Upload,
  FileVideo, FileAudio, X, Image, Calendar,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Poll GET /episodes/{id} until upload_status is 'ready', update item state along the way */
async function pollUntilReady(episodeId, localId, accessToken, intervalMs = 4000, maxWaitMs = 3600000) {
  const { updateItem } = useUploadStore.getState();
  const deadline = Date.now() + maxWaitMs;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, intervalMs));
    try {
      const res = await axios.get(`${API}/episodes/creator/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const ep = res.data?.find?.((e) => e.id === episodeId);
      if (!ep) continue;

      const uploadStatus = ep.upload_status;
      if (uploadStatus === 'ready') {
        updateItem(localId, { state: 'ready' });
        return;
      }
      if (uploadStatus === 'transcoding') {
        updateItem(localId, { state: 'processing', processingLabel: 'Transkóduji na 1080p…' });
      } else {
        updateItem(localId, { state: 'processing', processingLabel: 'Zpracovávám soubor…' });
      }
    } catch (_) {
      // ignore transient errors, keep polling
    }
  }
  // Timed out — optimistically mark ready anyway
  updateItem(localId, { state: 'ready' });
}

const ALLOWED_TYPES = {
  'video/mp4': 'video',
  'video/webm': 'video',
  'video/quicktime': 'video',
  'audio/mpeg': 'audio',
  'audio/wav': 'audio',
  'audio/ogg': 'audio',
  'audio/mp3': 'audio',
};

/** Extract media duration via an HTML element */
function extractDuration(file) {
  return new Promise((resolve) => {
    const mediaType = ALLOWED_TYPES[file.type];
    const el = document.createElement(mediaType === 'video' ? 'video' : 'audio');
    el.preload = 'metadata';
    const url = URL.createObjectURL(file);
    el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Math.round(el.duration) || 0); };
    el.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
    el.src = url;
  });
}

function StateIcon({ state }) {
  if (state === 'uploading' || state === 'processing' || state === 'publishing')
    return <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />;
  if (state === 'done')
    return <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />;
  if (state === 'error')
    return <XCircle className="w-4 h-4 text-destructive flex-shrink-0" />;
  return null;
}

function UploadItem({ item }) {
  const { updateItem, removeItem } = useUploadStore();
  const { accessToken } = useAuth();
  const thumbInputRef = useRef(null);

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    updateItem(item.localId, {
      thumbnailFile: file,
      thumbnailPreview: URL.createObjectURL(file),
    });
    e.target.value = '';
  };

  const handlePublish = async () => {
    const { episodeId, title, description, isFree, scheduledAt, thumbnailFile, tosAccepted } = item;
    if (!tosAccepted) return;

    updateItem(item.localId, { state: 'publishing' });

    try {
      // Upload thumbnail if provided
      let thumbnailUrl = null;
      if (thumbnailFile) {
        const presignRes = await axios.post(
          `${API}/episodes/presign-thumbnail`,
          { episode_id: episodeId, file_name: thumbnailFile.name, content_type: thumbnailFile.type },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        await axios.put(presignRes.data.upload_url, thumbnailFile, {
          headers: { 'Content-Type': thumbnailFile.type },
          withCredentials: false,   // S3 doesn't support credentialled CORS
        });
        thumbnailUrl = presignRes.data.thumbnail_url;
      }

      // Update metadata
      const patchBody = { title, description, is_free: isFree };
      if (thumbnailUrl) patchBody.thumbnail_url = thumbnailUrl;
      await axios.patch(`${API}/episodes/${episodeId}`, patchBody, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Publish (with optional scheduled_at)
      const publishBody = scheduledAt ? { scheduled_at: new Date(scheduledAt).toISOString() } : {};
      await axios.post(`${API}/episodes/${episodeId}/publish`, publishBody, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      updateItem(item.localId, { state: 'done' });
      window.dispatchEvent(new CustomEvent('episode-published'));

      // Auto-remove after 3s
      setTimeout(() => removeItem(item.localId), 3000);
    } catch (err) {
      updateItem(item.localId, {
        state: 'error',
        error: err.response?.data?.detail || 'Publikování selhalo',
      });
    }
  };

  const mediaType = ALLOWED_TYPES[item.file?.type] || 'audio';
  const FileIcon = mediaType === 'video' ? FileVideo : FileAudio;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2 bg-secondary/30">
        <FileIcon className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-sm truncate flex-1" title={item.fileName}>{item.fileName}</span>
        <StateIcon state={item.state} />
        {(item.state === 'done' || item.state === 'error') && (
          <button onClick={() => removeItem(item.localId)} className="text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Progress bar */}
      {item.state === 'uploading' && (
        <div className="px-3 py-2">
          <Progress value={item.progress} className="h-1.5" />
          <p className="text-xs text-muted-foreground mt-1">{item.progress}% nahráno</p>
        </div>
      )}

      {/* Processing */}
      {item.state === 'processing' && (
        <div className="px-3 py-2">
          <p className="text-xs text-muted-foreground">{item.processingLabel || 'Zpracovávám soubor…'}</p>
        </div>
      )}

      {/* Error */}
      {item.state === 'error' && (
        <div className="px-3 py-2">
          <p className="text-xs text-destructive">{item.error}</p>
        </div>
      )}

      {/* Done */}
      {item.state === 'done' && (
        <div className="px-3 py-2">
          <p className="text-xs text-green-500">
            {item.scheduledAt ? 'Naplánováno k publikování' : 'Publikováno'}
          </p>
        </div>
      )}

      {/* Publish form — shown when ready */}
      {item.state === 'ready' && (
        <div className="px-3 py-3 space-y-3 bg-background/50">
          {/* Title */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Název epizody</Label>
            <Input
              value={item.title}
              onChange={(e) => updateItem(item.localId, { title: e.target.value })}
              className="h-8 text-sm"
              placeholder="Název epizody"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Popis (volitelný)</Label>
            <textarea
              value={item.description}
              onChange={(e) => updateItem(item.localId, { description: e.target.value })}
              className="w-full min-h-[60px] text-sm bg-secondary/30 border border-border rounded-md px-3 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="Popis epizody…"
            />
          </div>

          {/* Thumbnail */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Náhledový obrázek (volitelný)</Label>
            <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={handleThumbnailChange} />
            {item.thumbnailPreview ? (
              <div className="relative w-full h-20 rounded-md overflow-hidden border border-border">
                <img src={item.thumbnailPreview} alt="thumbnail" className="w-full h-full object-cover" />
                <button
                  onClick={() => updateItem(item.localId, { thumbnailFile: null, thumbnailPreview: null })}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => thumbInputRef.current?.click()}
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border rounded-md px-3 py-2 w-full transition-colors"
              >
                <Image className="w-3.5 h-3.5" />
                Přidat náhledový obrázek
              </button>
            )}
          </div>

          {/* is_free toggle */}
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Bezplatná epizoda</Label>
            <Switch
              checked={item.isFree}
              onCheckedChange={(v) => updateItem(item.localId, { isFree: v })}
            />
          </div>

          {/* Scheduled date */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Datum zveřejnění (volitelný)
            </Label>
            <Input
              type="datetime-local"
              value={item.scheduledAt}
              onChange={(e) => updateItem(item.localId, { scheduledAt: e.target.value })}
              min={new Date().toISOString().slice(0, 16)}
              className="h-8 text-sm"
            />
          </div>

          {/* TOS */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={item.tosAccepted}
              onChange={(e) => updateItem(item.localId, { tosAccepted: e.target.checked })}
              className="mt-0.5"
            />
            <span className="text-xs text-muted-foreground">
              Souhlasím s{' '}
              <Link to="/content-guidelines" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                obsahovými pravidly
              </Link>
            </span>
          </label>

          {/* Publish button */}
          <Button
            size="sm"
            className="w-full bg-primary hover:bg-primary/90 text-sm"
            disabled={!item.tosAccepted || !item.title.trim()}
            onClick={handlePublish}
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {item.scheduledAt ? 'Naplánovat' : 'Publikovat'}
          </Button>
        </div>
      )}
    </div>
  );
}

export function UploadManager() {
  const { items, isOpen, setOpen } = useUploadStore();
  const { accessToken } = useAuth();
  const runningRef = useRef(new Set());
  const [collapsed, setCollapsed] = useState(false);

  const activeCount = items.filter((i) => ['uploading', 'processing', 'publishing'].includes(i.state)).length;
  const readyCount = items.filter((i) => i.state === 'ready').length;

  // Upload pipeline
  useEffect(() => {
    const queued = items.filter((i) => i.state === 'queued' && !runningRef.current.has(i.localId));
    queued.forEach((item) => {
      runningRef.current.add(item.localId);
      runUpload(item).finally(() => runningRef.current.delete(item.localId));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  async function runUpload(item) {
    const { updateItem } = useUploadStore.getState();

    try {
      updateItem(item.localId, { state: 'uploading', progress: 0 });

      // 1. Extract duration
      const duration = await extractDuration(item.file);
      const mediaType = ALLOWED_TYPES[item.file.type] || 'audio';

      // 2. Create draft episode + get presigned URL
      const draftRes = await axios.post(
        `${API}/episodes/draft`,
        {
          title: item.title || item.fileName.replace(/\.[^/.]+$/, ''),
          filename: item.fileName,
          content_type: item.file.type,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const { id: episode_id, media_url: upload_url } = draftRes.data;
      updateItem(item.localId, { episodeId: episode_id, mediaType, duration });

      // 3. Upload to S3 via presigned PUT URL
      //
      // IMPORTANT: withCredentials must be false here.
      //   axios.defaults.withCredentials is true (needed for HttpOnly auth
      //   cookies to our own backend), but that flag cannot be used for
      //   cross-origin requests to AWS S3 because:
      //     a) S3 never returns Access-Control-Allow-Credentials: true
      //     b) Browsers reject credentialled cross-origin responses that lack
      //        that header, turning it into a silent network error with no
      //        err.response → the catch block falls back to 'Nahrávání selhalo'
      //
      if (!upload_url) {
        throw Object.assign(new Error('S3 není nakonfigurováno'), {
          response: { data: { detail: 'Upload URL nebyla vygenerována – S3 není nakonfigurováno' } },
        });
      }
      await axios.put(upload_url, item.file, {
        headers: { 'Content-Type': item.file.type },
        withCredentials: false,   // must NOT send cookies to S3
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded / e.total) * 100);
          updateItem(item.localId, { progress: pct });
        },
      });

      // 4. Notify backend upload complete
      updateItem(item.localId, { state: 'processing' });
      const completeRes = await axios.post(
        `${API}/upload/complete/${episode_id}`,
        { client_duration: duration },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // 5. Route on the backend's reported status
      const uploadStatus = completeRes.data?.status;

      if (uploadStatus === 'pending') {
        // ffprobe returned 0 — episode is in DB and S3 but cannot be published
        // until duration is verified. This normally means ffprobe is not installed
        // on the server. In dev, this should not happen (dev fallback is active).
        updateItem(item.localId, {
          state: 'error',
          error: completeRes.data?.message ||
            'Délka média nebyla ověřena. Nainstaluj ffprobe na server nebo použij opravu délky v editoru.',
        });
      } else if (mediaType === 'video' && uploadStatus === 'processing') {
        // Backend started async transcoding — poll until upload_status → 'ready'
        updateItem(item.localId, { state: 'processing' });
        await pollUntilReady(episode_id, item.localId, accessToken);
      } else {
        updateItem(item.localId, { state: 'ready' });
      }
    } catch (err) {
      const { updateItem: upd } = useUploadStore.getState();
      upd(item.localId, {
        state: 'error',
        error: err.response?.data?.detail || 'Nahrávání selhalo',
      });
    }
  }

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-24 right-4 z-40 w-80 shadow-2xl rounded-xl overflow-hidden border border-border bg-card">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-secondary/50 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          {activeCount > 0 && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          <span className="text-sm font-medium">
            Nahrávání
            {activeCount > 0 && <span className="ml-1.5 text-xs text-primary">({activeCount} aktivní)</span>}
            {readyCount > 0 && <span className="ml-1.5 text-xs text-yellow-500">({readyCount} čeká na publikování)</span>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="text-muted-foreground hover:text-foreground p-0.5"
            title="Skrýt panel (nahrávání pokračuje)"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          {collapsed ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>

      {/* Items */}
      {!collapsed && (
        <div className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
          {items.map((item) => (
            <UploadItem key={item.localId} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
